/**
 * 数据访问层（迁移期兼容层）
 *
 * 页面里写的 `supabase.from('orders').select('*').eq('user_id', id)` 之类的调用
 * 现在全部转发到自建后端（api.jacvipac.cc / server 目录）上的 MySQL。
 *
 * 设计要点：
 * 1. 对外仍然导出名为 `supabase` 的对象，页面代码一行都不用改
 * 2. 查询链（select / insert / update / delete / eq / order / maybeSingle ...）
 *    在本地组装成过滤器，最终一次性打到 /api/data/:table/*
 * 3. 登录态取自自建后端的 JWT（src/lib/api.ts），不再依赖 Supabase Session
 * 4. 只有「还没迁移的部分」（邮件云函数、旧账号密码桥接）才会真正连 Supabase，
 *    这部分在第三阶段会一起清掉，届时可以整段删除
 */
import { createClient, type SupabaseClient } from '@supabase/supabase-js';
import {
  API_BASE,
  API_URL,
  getToken,
  getCachedUser,
  clearSession,
  subscribeAuth,
  type ApiUser,
} from '@/lib/api';

const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;
const supabaseAnonKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY as string;

// ============================================================
// 真实 Supabase 客户端（仅用于迁移期桥接，不再承载业务数据）
// ============================================================
let legacyClient: SupabaseClient | null = null;

export function getSupabase(): SupabaseClient {
  if (!legacyClient) {
    legacyClient = createClient(supabaseUrl, supabaseAnonKey, {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: false,
        flowType: 'pkce',
      },
    });
  }
  return legacyClient;
}

// ============================================================
// 后端请求
// ============================================================
const REQUEST_TIMEOUT = 20000;

/** 回退提示只打一次，避免控制台刷屏 */
let legacyWarned = false;

export class QueryError extends Error {
  status: number;

  code: string;

  constructor(message: string, status = 0) {
    super(message);
    this.name = 'QueryError';
    this.status = status;
    this.code = String(status);
  }
}

async function callApi<T>(
  path: string,
  body: Record<string, unknown>,
  withAuth = true,
): Promise<T> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT);

  try {
    const headers: Record<string, string> = {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    };
    const token = getToken();
    if (withAuth && token) headers.Authorization = `Bearer ${token}`;

    const res = await fetch(`${API_URL}${path}`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
      signal: controller.signal,
    });

    const text = await res.text();
    let parsed: Record<string, unknown> | null = null;
    if (text) {
      try {
        parsed = JSON.parse(text) as Record<string, unknown>;
      } catch {
        parsed = null;
      }
    }

    if (!res.ok) {
      const message = String(
        (parsed?.error as string)
        || (parsed?.detail as string)
        || text
        || `请求失败(${res.status})`,
      );
      throw new QueryError(message, res.status);
    }

    return (parsed || {}) as T;
  } catch (err) {
    if ((err as Error)?.name === 'AbortError') {
      throw new QueryError('网络超时，请稍后重试', 0);
    }
    throw err;
  } finally {
    clearTimeout(timer);
  }
}

// ============================================================
// 查询构造器（模拟 supabase-js 的链式 API）
// ============================================================
type Row = Record<string, unknown>;

interface FilterItem {
  col: string;
  op: string;
  value: unknown;
}

type Action = 'select' | 'insert' | 'update' | 'delete';

type QueryResult<T> = { data: T; error: QueryError | null };

class QueryBuilder implements PromiseLike<QueryResult<unknown>> {
  private table: string;

  private action: Action = 'select';

  private columns = '*';

  private filters: FilterItem[] = [];

  private orders: { col: string; ascending: boolean }[] = [];

  private limitCount: number | null = null;

  private payload: Row[] | Row | null = null;

  private mode: 'many' | 'single' | 'maybeSingle' = 'many';

  constructor(table: string) {
    this.table = table;
  }

  select(columns = '*') {
    this.columns = columns || '*';
    if (this.action !== 'insert' && this.action !== 'update' && this.action !== 'delete') {
      this.action = 'select';
    }
    return this;
  }

  insert(rows: Row | Row[]) {
    this.action = 'insert';
    this.payload = rows;
    return this;
  }

  update(values: Row) {
    this.action = 'update';
    this.payload = values;
    return this;
  }

  delete() {
    this.action = 'delete';
    return this;
  }

  upsert(rows: Row | Row[]) {
    return this.insert(rows);
  }

  eq(col: string, value: unknown) {
    this.filters.push({ col, op: 'eq', value });
    return this;
  }

  neq(col: string, value: unknown) {
    this.filters.push({ col, op: 'neq', value });
    return this;
  }

  gt(col: string, value: unknown) {
    this.filters.push({ col, op: 'gt', value });
    return this;
  }

  gte(col: string, value: unknown) {
    this.filters.push({ col, op: 'gte', value });
    return this;
  }

  lt(col: string, value: unknown) {
    this.filters.push({ col, op: 'lt', value });
    return this;
  }

  lte(col: string, value: unknown) {
    this.filters.push({ col, op: 'lte', value });
    return this;
  }

  like(col: string, value: unknown) {
    this.filters.push({ col, op: 'like', value });
    return this;
  }

  ilike(col: string, value: unknown) {
    this.filters.push({ col, op: 'like', value });
    return this;
  }

  is(col: string, value: unknown) {
    this.filters.push({ col, op: 'is', value });
    return this;
  }

  in(col: string, value: unknown[]) {
    this.filters.push({ col, op: 'in', value });
    return this;
  }

  order(col: string, options?: { ascending?: boolean }) {
    this.orders.push({ col, ascending: options?.ascending !== false });
    return this;
  }

  limit(count: number) {
    this.limitCount = count;
    return this;
  }

  single() {
    this.mode = 'single';
    return this;
  }

  maybeSingle() {
    this.mode = 'maybeSingle';
    return this;
  }

  then<TResult1 = QueryResult<unknown>, TResult2 = never>(
    onfulfilled?: ((value: QueryResult<unknown>) => TResult1 | PromiseLike<TResult1>) | null,
    onrejected?: ((reason: unknown) => TResult2 | PromiseLike<TResult2>) | null,
  ): PromiseLike<TResult1 | TResult2> {
    return this.execute().then(onfulfilled, onrejected);
  }

  /**
   * 兜底：自建后端不可达时（网络层失败），读取操作退回 Supabase。
   * 只兜底 select，写操作绝不回退，避免数据写到两个地方。
   */
  private async legacySelect(): Promise<QueryResult<unknown>> {
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let builder: any = getSupabase().from(this.table).select(this.columns);

      this.filters.forEach((f) => {
        switch (f.op) {
          case 'neq': builder = builder.neq(f.col, f.value); break;
          case 'gt': builder = builder.gt(f.col, f.value); break;
          case 'gte': builder = builder.gte(f.col, f.value); break;
          case 'lt': builder = builder.lt(f.col, f.value); break;
          case 'lte': builder = builder.lte(f.col, f.value); break;
          case 'like': builder = builder.like(f.col, f.value); break;
          case 'is': builder = builder.is(f.col, f.value); break;
          case 'in': builder = builder.in(f.col, f.value); break;
          default: builder = builder.eq(f.col, f.value); break;
        }
      });

      this.orders.forEach((o) => {
        builder = builder.order(o.col, { ascending: o.ascending });
      });
      if (this.limitCount !== null) builder = builder.limit(this.limitCount);
      if (this.mode === 'single') builder = builder.single();
      if (this.mode === 'maybeSingle') builder = builder.maybeSingle();

      const { data, error } = await builder;
      if (error) return { data: null, error: new QueryError(String(error.message || error), 0) };
      return { data, error: null };
    } catch (err) {
      return { data: null, error: new QueryError(String((err as Error)?.message || err), 0) };
    }
  }

  private async execute(): Promise<QueryResult<unknown>> {
    try {
      switch (this.action) {
        case 'insert': {
          const rows = Array.isArray(this.payload) ? this.payload : [this.payload as Row];
          const res = await callApi<{ data: Row[] }>(`/data/${this.table}/insert`, { rows });
          const list = res?.data || [];
          return { data: this.pick(list), error: null };
        }
        case 'update': {
          await callApi(`/data/${this.table}/update`, {
            values: this.payload,
            filters: this.filters,
          });
          return { data: null, error: null };
        }
        case 'delete': {
          await callApi(`/data/${this.table}/delete`, { filters: this.filters });
          return { data: null, error: null };
        }
        default: {
          const body: Record<string, unknown> = {
            columns: this.columns,
            filters: this.filters,
          };
          if (this.orders.length) body.order = this.orders;
          if (this.limitCount !== null) body.limit = this.limitCount;

          const res = await callApi<{ data: Row[] }>(`/data/${this.table}/select`, body);
          const list = res?.data || [];
          return { data: this.pick(list), error: null };
        }
      }
    } catch (err) {
      const message = String((err as Error)?.message || err);
      const status = (err as QueryError)?.status || 0;

      // 网络层失败（status 0）或后端 5xx 时，读取退回 Supabase，保证页面不白屏
      if (this.action === 'select' && (status === 0 || status >= 500)) {
        const fallback = await this.legacySelect();
        if (!fallback.error) {
          if (!legacyWarned) {
            legacyWarned = true;
            // eslint-disable-next-line no-console
            console.warn('[data] 自建后端暂不可用，已临时回退 Supabase 读取');
          }
          return fallback;
        }
      }

      return { data: null, error: new QueryError(message, status) };
    }
  }

  /** 按 single / maybeSingle 收敛返回值 */
  private pick(list: Row[]): unknown {
    if (this.mode === 'many') return list;
    return list.length ? list[0] : null;
  }
}

// ============================================================
// 登录态（对接自建后端 JWT）
// ============================================================
function toAuthUser(user: ApiUser) {
  return {
    id: user.id,
    email: user.email,
    phone: user.phone,
    role: user.role,
  };
}

const auth = {
  async getSession() {
    const token = getToken();
    const user = getCachedUser();
    if (!token || !user) return { data: { session: null }, error: null };
    return {
      data: {
        session: {
          access_token: token,
          token_type: 'bearer',
          user: toAuthUser(user),
        },
      },
      error: null,
    };
  },

  async getUser() {
    const user = getCachedUser();
    if (!getToken() || !user) {
      return { data: { user: null }, error: new QueryError('未登录', 401) };
    }
    return { data: { user: toAuthUser(user) }, error: null };
  },

  async signOut() {
    clearSession();
    try {
      await getSupabase().auth.signOut();
    } catch {
      // 桥接失败不影响本地登出
    }
    return { error: null };
  },

  // 仅用于迁移期：老账号密码仍在 Supabase 校验
  signInWithPassword(credentials: { email: string; password: string }) {
    return getSupabase().auth.signInWithPassword(credentials);
  },

  onAuthStateChange(callback: (event: string, session: unknown) => void) {
    const unsubscribe = subscribeAuth((user) => {
      callback(
        user ? 'SIGNED_IN' : 'SIGNED_OUT',
        user ? { access_token: getToken(), user: toAuthUser(user) } : null,
      );
    });
    return { data: { subscription: { unsubscribe } } };
  },
};

// ============================================================
// 文件上传（替代 Supabase Storage）
// ============================================================
const uploadedUrls = new Map<string, string>();

function publicUrlOf(path: string): string {
  const cached = uploadedUrls.get(path);
  if (cached) return cached;
  return `${API_BASE}/uploads/${String(path).replace(/^\/+/, '')}`;
}

const storage = {
  from(bucket: string) {
    return {
      async upload(
        path: string,
        file: File | Blob,
        _options?: Record<string, unknown>,
      ) {
        try {
          const form = new FormData();
          const filename = (file as File)?.name || `${bucket}-${Date.now()}.jpg`;
          form.append('file', file, filename);

          const headers: Record<string, string> = {};
          const token = getToken();
          if (token) headers.Authorization = `Bearer ${token}`;

          const res = await fetch(`${API_URL}/upload`, {
            method: 'POST',
            headers,
            body: form,
          });
          const text = await res.text();
          let parsed: { url?: string; path?: string; error?: string } | null = null;
          try {
            parsed = text ? JSON.parse(text) : null;
          } catch {
            parsed = null;
          }

          if (!res.ok || !parsed?.url) {
            return {
              data: null,
              error: new QueryError(parsed?.error || '文件上传失败', res.status),
            };
          }

          uploadedUrls.set(path, parsed.url);
          return { data: { path: parsed.path || path, fullPath: parsed.url }, error: null };
        } catch (err) {
          return { data: null, error: new QueryError(String((err as Error)?.message || err), 0) };
        }
      },

      getPublicUrl(path: string) {
        return { data: { publicUrl: publicUrlOf(path) } };
      },
    };
  },
};

// ============================================================
// 云函数桥接（邮件 / 后台，第二阶段迁移）
// ============================================================
const functions = {
  invoke(name: string, options?: Record<string, unknown>) {
    return getSupabase().functions.invoke(name, options);
  },
};

// ============================================================
// 对外导出：keep 原来的名字，页面无需修改
// ============================================================
export const supabase = {
  from(table: string) {
    return new QueryBuilder(table);
  },
  auth,
  storage,
  functions,
  channel: () => ({
    on: () => ({ subscribe: () => ({ unsubscribe: () => {} }) }),
    subscribe: () => ({ unsubscribe: () => {} }),
  }),
  removeChannel: () => Promise.resolve(),
};

/**
 * 带重试的查询包装器（保留原签名，兼容旧调用）
 */
export async function withRetry<T>(
  fn: () => Promise<{ data: T | null; error: unknown }>,
  retries = 3,
  delayMs = 1000,
): Promise<{ data: T | null; error: unknown }> {
  let lastResult: { data: T | null; error: unknown } = { data: null, error: null };
  for (let attempt = 0; attempt < retries; attempt += 1) {
    try {
      lastResult = await fn();
      if (!lastResult.error) return lastResult;
      const errMsg = String(
        (lastResult.error as { message?: string })?.message || lastResult.error,
      ).toLowerCase();
      const isTransient = errMsg.includes('network')
        || errMsg.includes('fetch')
        || errMsg.includes('timeout')
        || errMsg.includes('超时')
        || errMsg.includes('failed');
      if (!isTransient) return lastResult;
    } catch (e) {
      lastResult = { data: null, error: e };
    }
    if (attempt < retries - 1) {
      // eslint-disable-next-line no-await-in-loop
      await new Promise((r) => setTimeout(r, delayMs * 2 ** attempt));
    }
  }
  return lastResult;
}